// src/layouts/LearnLayout.tsx
//
// The learning view. As soon as anyone opens lessons, the portal's sidebar
// and top bar give way to a quiet, full-width page with a slim header, so the
// screen is about the lesson and nothing else.
//
//   standard  parents (with an "Exit lessons" button back to the dashboard),
//             students and learners (their whole portal is this view)
//   child     child mode: a parent's device handed to a young child; bigger
//             text, and a grown-up question is needed to leave

import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, ChevronDown, LayoutDashboard, Lock, LogOut, Settings, Smile } from "lucide-react";
import { clearCredentials, selectUser } from "../app/features/authSlice";
import { useLogoutMutation } from "../app/api/authApi";
import { endChildMode, grownUpQuestion } from "../app/childMode";

function GrownUpGate({ onUnlock, onClose }: { onUnlock: () => void; onClose: () => void }) {
  const [q] = useState(grownUpQuestion);
  const [value, setValue] = useState("");
  const [wrong, setWrong] = useState(false);
  const check = (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(value) === q.answer) onUnlock();
    else {
      setWrong(true);
      setValue("");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#16213e]/50 p-4" onClick={onClose}>
      <form onSubmit={check} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-[28px] bg-white p-6 text-center">
        <Lock className="mx-auto h-8 w-8 text-[#1b2d5e]" />
        <p className="mt-3 text-lg font-bold text-[#16213e]">To leave child mode, answer:</p>
        <p className="mt-3 text-3xl font-bold text-[#1b2d5e]">{q.text} = ?</p>
        <input
          autoFocus
          inputMode="numeric"
          aria-label="Answer"
          value={value}
          onChange={(e) => {
            setValue(e.target.value.replace(/\D/g, ""));
            setWrong(false);
          }}
          className="mt-4 w-full rounded-2xl border border-[#dde2ec] px-4 py-3 text-center text-xl outline-none focus:border-[#1b2d5e]"
        />
        {wrong && <p className="mt-2 text-sm font-semibold text-[#b94a3a]">Not quite. Try again.</p>}
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#3b4560]">
            Back
          </button>
          <button type="submit" className="flex-1 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-bold text-white">
            Unlock
          </button>
        </div>
      </form>
    </div>
  );
}

/** Students and learners: their name, settings (students) and sign out. */
function AccountMenu() {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const signOut = async () => {
    try {
      await logout().unwrap();
    } catch {
      // continue regardless: the token is stateless
    }
    dispatch(clearCredentials());
    navigate("/login");
  };

  const initial = (user?.firstName?.charAt(0) ?? "?").toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-2 rounded-2xl bg-white px-2 py-1.5 ring-1 ring-[#dde2ec] transition hover:bg-[#f9fafc]"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#1b2d5e] text-sm font-bold text-white">{initial}</span>
        <span className="hidden text-sm font-semibold text-[#16213e] sm:block">{user?.firstName}</span>
        <ChevronDown className={`h-4 w-4 text-[#8189a0] transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_16px_48px_rgba(27,45,94,0.16)] ring-1 ring-[#dde2ec]">
          {user?.role === "student" && (
            <Link
              to="/user/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#3b4560] hover:bg-[#f1f3f8]"
            >
              <Settings className="h-4 w-4" /> Settings
            </Link>
          )}
          <button
            type="button"
            onClick={signOut}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#c0392b] hover:bg-[#fff5f5]"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function LearnLayout({ child }: { child?: { childId: string; firstName: string } }) {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [gate, setGate] = useState(false);
  const isParent = user?.role === "parent" && !child;
  const home = child
    ? `/user/learn/${child.childId}`
    : user?.role === "learner"
      ? `/user/learn/${user.childId ?? ""}`
      : "/user/learn";

  // Each page starts at the top (links to a #section scroll there instead)
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  // Child mode: bigger text everywhere while a young child is using the screen
  useEffect(() => {
    if (!child) return;
    const root = document.documentElement;
    const before = root.style.fontSize;
    root.style.fontSize = "18px";
    return () => {
      root.style.fontSize = before;
    };
  }, [child]);

  return (
    <div className="min-h-screen bg-[#f5f4ef] font-sans text-[#16213e] antialiased">
      <header className="sticky top-0 z-30 border-b border-[#e5e8ef] bg-[#f5f4ef]/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to={home} className="flex min-w-0 items-center gap-2.5">
            {child ? (
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#1b2d5e] text-white">
                <Smile className="h-6 w-6" />
              </span>
            ) : (
              <img src="/images/logo.png" alt="" className="h-9 w-9 flex-shrink-0 rounded-xl" />
            )}
            <span className="min-w-0">
              <span className="block truncate text-[15px] font-extrabold tracking-[-0.02em] text-[#16213e]">
                {child ? `${child.firstName}'s lessons` : "Future Smart Lab"}
              </span>
              {!child && (
                <span className="flex items-center gap-1 text-[11.5px] font-semibold uppercase tracking-[0.12em] text-[#8189a0]">
                  <BookOpen className="h-3 w-3" /> Lessons
                </span>
              )}
            </span>
          </Link>

          {child ? (
            <button
              type="button"
              onClick={() => setGate(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-[#5f6780] ring-1 ring-[#dde2ec]"
            >
              <Lock className="h-4 w-4" /> Grown-ups
            </button>
          ) : isParent ? (
            <Link
              to="/user/dashboard"
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-[#1b2d5e] ring-1 ring-[#dde2ec] transition hover:bg-[#f9fafc]"
            >
              <LayoutDashboard className="h-4 w-4" /> <span><span className="hidden sm:inline">Back to </span>dashboard</span>
            </Link>
          ) : (
            <AccountMenu />
          )}
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6 sm:px-6 sm:pt-8">
        <Outlet />
      </main>

      {gate && child && (
        <GrownUpGate
          onClose={() => setGate(false)}
          onUnlock={() => {
            endChildMode();
            navigate(`/user/learn/${child.childId}`, { replace: true });
          }}
        />
      )}
    </div>
  );
}
